import type { Outcome } from '../types.js';
import { HERO_OUTCOMES_EXTRA } from './heroes-extra.js';
import generatedHeroes from '../../content/hero-outcomes-generated.json' with { type: 'json' };

/** Hand-authored hero outcomes for high-traffic combos (subset; keyed exactly). */
export const HERO_OUTCOMES: Outcome[] = [
  {
    key: 'book1_narrator_quits/b03_bard/twist_twin/hope_low',
    tier: 'hero',
    bridgeText:
      'Tomas freezes mid-chord. "I had a twin?" he whispers. From behind a pillar steps another bard, identical except for a scar shaped like a semicolon. The narrator hisses that this was not scheduled.',
    narratorQuip: 'Twins were for Act Two. You are rude.',
    effects: [
      { type: 'set_variable', target: 'bard_alive', value: true },
      { type: 'shift_track', target: 'chaos', value: 2 },
      { type: 'narrator_glitch', value: 'twin_reveal' },
      { type: 'scene_mood', value: 'chaos' },
    ],
    callback: 'The twin bard still owes the plot a chorus.',
    visual: {
      backgroundId: 's03_bard',
      mood: 'chaos',
      overlayIds: ['overlay_twin'],
      narratorExpression: 'flustered',
      stampEffect: 'stamp_twist',
    },
  },
  {
    key: 'book1_narrator_quits/b03_mood/rule_skip_intent/hope_low',
    tier: 'hero',
    bridgeText:
      'You slap Cut the Foreshadowing onto the stairwell tragedy before it can load. Tomas skids, catches his lute, and bows to no one. The narrator flips pages backward, offended.',
    narratorQuip: 'I had a whole death monologue!',
    effects: [
      { type: 'set_variable', target: 'intent_blocked', value: true },
      { type: 'shift_track', target: 'hope', value: 2 },
      { type: 'shift_dread', value: -1 },
      { type: 'narrator_glitch', value: 'intent_cancel' },
    ],
    visual: {
      backgroundId: 's03_bard',
      mood: 'triumph',
      overlayIds: ['overlay_rule_break'],
      narratorExpression: 'angry',
    },
  },
  {
    key: 'book1_narrator_quits/b02_villain/twist_frog_curse/hope_mid',
    tier: 'hero',
    bridgeText:
      'Archivist Vell mid-smile becomes Archivist Vell mid-ribbit. The ledger floats, unimpressed. Candle wax drips in the shape of a tiny crown.',
    narratorQuip: 'Royalty cannot be amphibians. Except now.',
    effects: [
      { type: 'set_variable', target: 'villain', value: 'Frog Vell' },
      { type: 'shift_track', target: 'chaos', value: 2 },
      { type: 'add_callback', target: 'villain', value: 'frog' },
      { type: 'scene_mood', value: 'chaos' },
    ],
    callback: 'Vell is still a frog. The Archive has opinions.',
    visual: {
      backgroundId: 's02_archive',
      mood: 'chaos',
      overlayIds: ['overlay_frog'],
      narratorExpression: 'flustered',
      stampEffect: 'stamp_twist',
    },
  },
  {
    key: 'book1_narrator_quits/b04_tower/chaos_gravity_off/chaos_low',
    tier: 'hero',
    bridgeText:
      'Gravity takes a union break. Ink pots float. Mira drifts upward with dignity she absolutely does not feel. The tower forgets which way is down.',
    narratorQuip: 'We are not doing physics today.',
    effects: [
      { type: 'shift_track', target: 'chaos', value: 3 },
      { type: 'shift_dread', value: 1 },
      { type: 'scene_mood', value: 'chaos' },
      { type: 'narrator_glitch', value: 'gravity_off' },
    ],
    visual: {
      backgroundId: 's04_tower',
      mood: 'chaos',
      overlayIds: ['overlay_gravity'],
      narratorExpression: 'panicked',
    },
  },
  {
    key: 'book1_narrator_quits/b01_gate/twist_actually_door/hope_low',
    tier: 'hero',
    bridgeText:
      'The gatekeeper reaches for a key that no longer exists because the Whisper Gate is now a door. It was always a door. The stone agrees, embarrassed.',
    narratorQuip: 'I wrote gate. You heard door. Typical.',
    effects: [
      { type: 'shift_track', target: 'fate', value: 1 },
      { type: 'narrator_glitch', value: 'repaint' },
      { type: 'scene_mood', value: 'tense' },
    ],
    visual: {
      backgroundId: 's01_gate',
      mood: 'tense',
      overlayIds: ['overlay_door'],
      narratorExpression: 'flustered',
    },
  },
  {
    key: 'book1_narrator_quits/b08_panic/rule_lie_mode/dread_high',
    tier: 'hero',
    bridgeText:
      'You grant the narrator permission to lie once. They use it immediately: "Mira never broke canon." The page contradicts itself in footnotes that catch fire politely.',
    narratorQuip: 'You made me lie. I will remember.',
    effects: [
      { type: 'shift_track', target: 'dread', value: 2 },
      { type: 'shift_dread', value: 2 },
      { type: 'narrator_glitch', value: 'lie_mode' },
      { type: 'scene_mood', value: 'dread' },
    ],
    visual: {
      backgroundId: 's08_reveal',
      mood: 'dread',
      overlayIds: ['overlay_ink_bleed'],
      narratorExpression: 'smug',
    },
  },
  {
    key: 'book1_narrator_quits/b07_villain/counter_nope/fate_mid',
    tier: 'hero',
    bridgeText:
      'Nope. The coronation un-happens with a rubber-band snap. Vell\'s crown hovers, confused. The narrator\'s victory speech dissolves into coughs.',
    narratorQuip: 'That was my favorite sentence.',
    effects: [
      { type: 'set_variable', target: 'intent_blocked', value: true },
      { type: 'shift_track', target: 'hope', value: 1 },
      { type: 'shift_dread', value: -1 },
      { type: 'narrator_glitch', value: 'nope' },
    ],
    visual: {
      backgroundId: 's07_throne',
      mood: 'triumph',
      overlayIds: ['overlay_nope'],
      narratorExpression: 'angry',
      stampEffect: 'stamp_nope',
    },
  },
  {
    key: 'book1_narrator_quits/b05_market/chaos_sudden_festival/chaos_mid',
    tier: 'hero',
    bridgeText:
      'The riot becomes a festival because drums appear from nowhere and everyone agrees to pretend motivation is fun. Extras dance badly. The trail of Vell is lost in confetti.',
    narratorQuip: 'I cannot fight confetti. I tried.',
    effects: [
      { type: 'shift_track', target: 'chaos', value: 2 },
      { type: 'scene_mood', value: 'chaos' },
      { type: 'narrator_glitch', value: 'festival' },
    ],
    visual: {
      backgroundId: 's05_market',
      mood: 'chaos',
      overlayIds: ['overlay_festival'],
      narratorExpression: 'flustered',
    },
  },
  {
    key: 'book1_narrator_quits/b06_choice/twist_wrong_hero/hope_high',
    tier: 'hero',
    bridgeText:
      'Wrong Hero swaps the cruel choice onto an extra who always wanted spotlight. Mira walks across the bridge untouched. The extra waves, thrilled, doomed, delighted.',
    narratorQuip: 'That was not Mira\'s sacrifice. Cheater.',
    effects: [
      { type: 'shift_track', target: 'hope', value: 2 },
      { type: 'shift_dread', value: -1 },
      { type: 'narrator_glitch', value: 'hero_swap' },
    ],
    visual: {
      backgroundId: 's06_bridge',
      mood: 'triumph',
      overlayIds: ['overlay_hero_swap'],
      narratorExpression: 'annoyed',
    },
  },
  {
    key: 'book1_narrator_quits/b24_hero/keep_lucky_charm/fate_low',
    tier: 'hero',
    bridgeText:
      'You press Lucky Charm into Mira\'s palm. The third wick steadies. Fate track hums like a plucked string. The narrator pretends charms are not canon.',
    narratorQuip: 'Luck is not a mechanic. It is now.',
    effects: [
      { type: 'apply_keepsake', target: 'hero', value: 'keep_lucky_charm' },
      { type: 'shift_track', target: 'fate', value: 2 },
      { type: 'shift_dread', value: -1 },
    ],
    callback: 'Mira\'s charm still warms against the wax.',
    visual: {
      backgroundId: 's10_choice',
      mood: 'calm',
      overlayIds: ['overlay_charm'],
      narratorExpression: 'wary',
    },
  },
  {
    key: 'book1_narrator_quits/b25_narrator/twist_helpful_stranger/dread_mid',
    tier: 'hero',
    bridgeText:
      'A too-helpful stranger steps into the last paragraph and hands the narrator a cup of tea. "You seem stressed," they say. The Narrator forgets to be ominous.',
    narratorQuip: 'I do not need hydration. I need control.',
    effects: [
      { type: 'shift_track', target: 'hope', value: 1 },
      { type: 'shift_track', target: 'chaos', value: 1 },
      { type: 'narrator_glitch', value: 'stranger' },
    ],
    visual: {
      backgroundId: 's11_finale',
      mood: 'tense',
      overlayIds: ['overlay_stranger'],
      narratorExpression: 'flustered',
    },
  },
  {
    key: 'book1_narrator_quits/b23_sacrifice/counter_not_yet/dread_high',
    tier: 'hero',
    bridgeText:
      'Not Yet delays the sacrifice until the storm runs out of ink. Tomas ducks, alive for now, offended on behalf of tragedies everywhere.',
    narratorQuip: 'Death can wait. Apparently.',
    effects: [
      { type: 'shift_dread', value: -2 },
      { type: 'set_variable', target: 'bard_alive', value: true },
      { type: 'shift_track', target: 'hope', value: 1 },
    ],
    visual: {
      backgroundId: 's09_storm',
      mood: 'tense',
      overlayIds: ['overlay_delay'],
      narratorExpression: 'gritting',
    },
  },
];

export const ALL_HERO_OUTCOMES = [
  ...(generatedHeroes as Outcome[]),
  ...HERO_OUTCOMES,
  ...HERO_OUTCOMES_EXTRA,
];

export const HERO_OUTCOME_MAP = new Map<string, Outcome>();
for (const o of ALL_HERO_OUTCOMES) {
  HERO_OUTCOME_MAP.set(o.key, o);
}
